import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { getFormattedDate } from '@experiences/util';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApBadge,
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridHeaderButton,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
} from '@uipath/portal-shell-react';
import React from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

import type { Consent } from '../../services/identity/ConsentService';
import { ApDataGridMoreRowActionsComponent } from '../common/ApDataGrid/ApDataGridMoreRowActionsComponent';
import {
    ButtonType,
    GridActionType,
} from '../common/UiGrid/grid';
import { ConsentCard } from './ConsentCard';
import { useConsentList } from './ConsentListViewModel';

const DATETIME_FORMAT = 'ddd, MMM D, YYYY HH:mm';

export const ConsentList = () => {
    const { formatMessage: translate } = useIntl();

    const EnableMultipleOrgConsents = useFeatureFlagValue(Features.EnableMultipleOrgConsents.name);

    const {
        consents,
        isLoading,
        onEdit,
        onDelete,
        onCreate,
        enableCreate,
        onActivateDeactivate,
        mutate,
        onViewUserRecords,
        isCardView,
        toggleCardView,
    } = useConsentList();

    return (
        <>
            <UiStack
                direction="column"
                align="start"
                gap={SpacingToken.M}>

                <UiText>
                    <FormattedMessage
                        id="CLIENT_USER_CONSENT_DETAILS"
                        values={{
                            bold: chunks => <UiText
                                display='inline'
                                variant={FontVariantToken.fontSizeMBold}>
                                {chunks}
                            </UiText>,
                        }}
                    />
                </UiText>

                {!EnableMultipleOrgConsents && <UiStack
                    wrap='wrap'
                    pt={SpacingToken.S}
                    gap={SpacingToken.XS}>
                    {consents?.map(consent => (
                        <ConsentCard
                            key={consent.id}
                            consent={consent}
                        />
                    ))}
                </UiStack>}
            </UiStack>

            {EnableMultipleOrgConsents && <ApDataGridWrapper<Consent>
                data={consents ?? []}
                selectable={false}
                loading={isLoading}
                refreshable
                refresh={() => mutate()}
                unpaginated
                useCardView={isCardView}
                showHeaderRow={!isCardView}
                cardViewRenderer={consent => <ConsentCard
                    consent={consent}
                    isCardView />}
                dataCy="consent-list-grid">
                <ApDataGridHeader<Consent> search>
                    <ApDataGridHeaderButton<Consent>
                        id='consent-view-button'
                        key='consent-view-button'
                        dataCy='consent-view-button'
                        type='main'
                        color='primary'
                        buttonType='mat-icon-button'
                        icon={isCardView ? 'table_view' : 'grid_view'}
                        text={isCardView ? translate({ id: 'CLIENT_TOGGLE_TABLE_VIEW' }) : translate({ id: 'CLIENT_TOGGLE_CARD_VIEW' })}
                        label={isCardView ? translate({ id: 'CLIENT_TOGGLE_TABLE_VIEW' }) : translate({ id: 'CLIENT_TOGGLE_CARD_VIEW' })}
                        visible
                        onClick={toggleCardView}
                    />
                    <ApDataGridHeaderButton<Consent>
                        id='consent-create-button'
                        key='consent-create-button'
                        type='main'
                        buttonType='mat-flat-button'
                        visible
                        disabled={!enableCreate}
                        color='primary'
                        text={translate({ id: 'CLIENT_USER_CONSENT_CREATE' })}
                        label={translate({ id: 'CLIENT_USER_CONSENT_CREATE' })}
                        onClick={onCreate}
                        dataCy='consent-create-button'
                        icon="add"
                    />
                </ApDataGridHeader>

                <ApDataGridColumn<Consent>
                    property="title"
                    title={translate({ id: 'CLIENT_CONSENT_TITLE' })}
                    searchable
                    render={(consent: Consent) => (
                        <UiText>
                            {consent.content?.title || translate({ id: 'CLIENT_UNTITLED' })}
                        </UiText>
                    )}
                />

                <ApDataGridColumn<Consent>
                    property="isActive"
                    title={translate({ id: 'CLIENT_STATUS' })}
                    render={(consent: Consent) => (
                        <ApBadge
                            status={consent.isActive ? 'success' : 'error'}
                            label={translate({ id: consent.isActive ? 'CLIENT_ACTIVE' : 'CLIENT_INACTIVE' })}
                        />
                    )}
                />

                <ApDataGridColumn<Consent>
                    property="userRecordCount"
                    title={translate({ id: 'CLIENT_CONSENT_USER_RECORDS_HEADER' })}
                    render={(consent: Consent) => (
                        <UiText>
                            {consent.userRecordCount ?? 0}
                        </UiText>
                    )}
                />

                <ApDataGridColumn<Consent>
                    property="creationTime"
                    title={translate({ id: 'CLIENT_CREATED_AT' })}
                    render={(consent: Consent) => (
                        <UiText>
                            {getFormattedDate(consent.creationTime, DATETIME_FORMAT)}
                        </UiText>
                    )}
                />

                <ApDataGridRowActions>
                    <ApDataGridRowButton<Consent>
                        id='row-actions'
                        render={(consent) => <ApDataGridMoreRowActionsComponent<Consent>
                            row={consent}
                            actions={[
                                {
                                    id: 'activate-deactivate',
                                    label: consent.isActive ? translate({ id: 'CLIENT_CONSENT_DEACTIVATE' }) : translate({ id: 'CLIENT_CONSENT_ACTIVATE' }),
                                    type: ButtonType.Button,
                                    actionType: GridActionType.Row,
                                    click: () => onActivateDeactivate(!consent.isActive, consent),
                                },
                                ...(consent.userRecordCount && consent.userRecordCount > 0 ? [
                                    {
                                        id: 'view-user-records',
                                        label: translate({ id: 'CLIENT_CONSENT_VIEW_USER_RECORDS' }),
                                        type: ButtonType.Button,
                                        actionType: GridActionType.Row,
                                        click: () => onViewUserRecords(consent.id),
                                    },
                                ] : []),
                                {
                                    id: 'edit',
                                    type: ButtonType.Button,
                                    actionType: GridActionType.Row,
                                    label: translate({ id: 'CLIENT_EDIT' }),
                                    click: () => onEdit(consent.id),
                                },
                                {
                                    id: 'delete',
                                    type: ButtonType.Button,
                                    actionType: GridActionType.Row,
                                    label: translate({ id: 'CLIENT_DELETE' }),
                                    click: () => onDelete(consent.id),
                                },
                            ]} />}
                    />
                </ApDataGridRowActions>
                <ApDataGridFooter
                    hidden
                    length={consents?.length ?? 0}
                    pageSizes={[]}
                />

            </ApDataGridWrapper>}
        </>
    );
};
